import React, { useState, useEffect, useRef } from "react";
import { Search } from "lucide-react";

export function InputTextField({ title, value, placeholder = null, type = "text", inputMode = null, allowPattern = null, pattern = null, validationTitle = null, min = null, max = null, validate=null, validateLive = false, onChange=null, onPaste=null, disabled, required = false, errorMessage=null, style={}, groupStyle={}, endIcon = null, focus = false, debounce = false, debounceTimeout = 300 }) {
    const [content, setContent] = useState(value || "");
    const [error, setError] = useState(null);
    const containerRef = useRef(null);
    const inputRef = useRef(null);
    const debounceTimerRef = useRef(null);
    
    useEffect(() => {
        if(content !== value){
            setContent(value);
        }
    }, [ value ]);

    // Focus the input when component mounts if focus prop is true
    useEffect(() => {
        if (focus && inputRef.current) {
            inputRef.current.focus();
        }
    }, [focus]);

    // Cleanup debounce timer on unmount
    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, []);

    function handleChange(e) {
        const newValue = e.target.value;

        if (allowPattern && !allowPattern.test(newValue)) {
            return;
        }
        
        if(onPaste && newValue.length > 4){
            onPaste(newValue);
        } else {
            // Always update local content immediately for responsive UI
            setContent(newValue);
            
            // Handle validation
            if(validate){
                    
                if(validate(newValue)){
                    setError(validate(newValue));
                } else {
                    setError(null);
                }
                
                if(validateLive){
                    if(!validate(newValue)){
                        setContent(newValue);
                    }
                }
            }
            
            // Handle onChange with debounce if enabled
            if(onChange){
                if(debounce) {
                    // Clear existing timer
                    if (debounceTimerRef.current) {
                        clearTimeout(debounceTimerRef.current);
                    }
                    
                    // Set new debounced timer
                    debounceTimerRef.current = setTimeout(() => {
                        onChange(newValue);
                    }, debounceTimeout);
                } else {
                    // Call onChange immediately if debounce is disabled
                    onChange(newValue);
                }
            }
        }
    }

    
    // For testing - if no endIcon provided, show search icon
    const iconToShow = endIcon || <Search size={16} />;

    return (
        <div className="input-group" style={{...groupStyle}} ref={containerRef}>
            {title && <label>{title}</label>}
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <input
                    ref={inputRef}
                    value={content}
                    className={`outline${error && content.length > 0 ? ' error' : ''}`}
                    placeholder={placeholder}
                    type={type}
                    inputMode={inputMode}
                    pattern={pattern}
                    title={validationTitle}
                    min={min}
                    max={max}
                    disabled={disabled}
                    required={required}
                    onChange={handleChange}
                    style={{paddingRight: endIcon ? '2.5rem' : '0.75rem' , ...style}}
                />
                {endIcon && (
                    <div className="input-end-icon">
                        {endIcon}
                    </div>
                )}
            </div>
            { error && errorMessage && <p className="error">{errorMessage}</p> }
        </div>
    );
}
